/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { isObjectOrArrayAndEmpty } from '../../../shared/Utils/Utils';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import {
  IDetailComponentProps,
  IEmaasEnergyUsageComponentDataV2,
  IEmaasEnergyUsageComponentDataArea,
  IAny
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { Card, Icon, Select, Tooltip } from '@scuf/common';
import { EnergyUsageField } from '../../../shared/Enums';
import EmaasNILMEnergyDisaggregationComponent from './EmaasNILMEnergyDisaggregationComponent';
import { IOption } from '@scuf/common/dist/components/Select/ISelectProps';

interface ITotalEmissionTabDetailsComponentProps {
  data: IEmaasEnergyUsageComponentDataV2;
  onRefresh: () => Promise<void>;
  nilmTab?: string;
  requestContext?: IAny;
  intervalType?: string;
}
const SiteEnergyAllUtilitiesDetailContent = (
  props: ITotalEmissionTabDetailsComponentProps
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, nilmTab, requestContext } = props;
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  return (
    <div className="carbondetail-performance-detail-container">
      <div>
        <EmaasNILMEnergyDisaggregationComponent
          data={data}
          requestContext={requestContext}
          intervalType={props.intervalType}
        />
      </div>
    </div>
  );
};

const SiteEnergyAllUtilitiesDetailWithLoader = withLoadingStatus(
  withErrorStatus(SiteEnergyAllUtilitiesDetailContent)
);

export interface IEmaasEnergyUsagesAllUtilityDetailWidgetProps
  extends IDetailComponentProps {
  isMeterListLoading: boolean;
  meterList: IOption[];
  selectedMeter: string | null;
  onMeterChange: (id: string) => void;
  onRefresh: () => Promise<void>;
  meterListStatusCode: number | null;
  intervalType?: string;
}

const emaasEnergyUsagesAllUtilityDetailWidget = (
  props: IEmaasEnergyUsagesAllUtilityDetailWidgetProps
) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const {
    dataConnector,
    nilmTab,
    requestContext,
    isMeterListLoading,
    meterList,
    selectedMeter,
    meterListStatusCode,
    onMeterChange,
    onRefresh // supports call for both data connectors, meterlist as well as disagg data
  } = props;

  const { all, loading, error, status } = dataConnector.data;
  const electricitySavings = (all as unknown) as IEmaasEnergyUsageComponentDataV2;
  const isDataAvailable = electricitySavings ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as IEmaasEnergyUsageComponentDataV2);

  useEffect(() => {
    if (meterListStatusCode && meterListStatusCode === 200) {
      setIsLoading(loading);
      if ((isDataAvailable || error || status) && !loading) {
        if (isDataAvailable) {
          setData(electricitySavings);
        }
        setErrorInfo(error);
        setStatusCode(status);
      }
    } else {
      setIsLoading(false);
      setStatusCode(meterListStatusCode);
    }
  }, [isDataAvailable, loading, status, meterListStatusCode]);

  return (
    <>
      <Card className="emaas-performance-detail-card energyDisaggregation">
        <Card.Header>
          <div className="emaas-summary-card-header nilm-header">
            <div className="header-title">
              <span>{props.headerTitle}</span>
              <span className="second-header">
                {t(d.ENERGYCONSUMPTIONSECONDHEADERNILM)}
              </span>
            </div>
            <div className="header-dropdown">
              <Select
                value={selectedMeter}
                className="meter-selector"
                options={meterList}
                onChange={onMeterChange}
              />
            </div>
          </div>
        </Card.Header>
        <Card.Content className="detail-summary-card">
          <SiteEnergyAllUtilitiesDetailWithLoader
            loading={isLoading || isMeterListLoading}
            error={errorInfo}
            statusCode={statusCode}
            onRefresh={onRefresh}
            data={data}
            loadingText={t(d.LOADING)}
            title={t(d.CONNECTIONERRORTITLE)}
            noDataMessage={
              meterListStatusCode === 204 ? t(d.NODATA) : t(d.NODATAAVAILABLE)
            }
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            nilmTab={nilmTab}
            requestContext={requestContext}
            intervalType={props.intervalType}
          />
        </Card.Content>
      </Card>
    </>
  );
};
export default emaasEnergyUsagesAllUtilityDetailWidget;
